import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CardHeader } from '../ui/CardHeader';
import { MeterBar } from '../ui/MeterBar';
import { StatTile } from '../ui/StatTile';
import { SurfaceCard } from '../ui/SurfaceCard';
import { useUnwrappedAngle } from '../../hooks/useUnwrappedAngle';
import { fontFamily } from '../../theme/tokens';
import type { WeatherView } from '../../weather/format';

const CARDINALS = [
  { label: 'N', x: 120, y: 44, primary: true },
  { label: 'E', x: 196, y: 120, primary: false },
  { label: 'S', x: 120, y: 196, primary: false },
  { label: 'V', x: 44, y: 120, primary: false },
] as const;

interface WindCardProps {
  view: WeatherView;
  onOpenHistory: () => void;
}

export function WindCard({ view, onOpenHistory }: WindCardProps) {
  const angle = useUnwrappedAngle(view.windAngle);

  return (
    <SurfaceCard sx={{ alignItems: 'stretch' }}>
      <CardHeader title="Vânt" onOpenHistory={onOpenHistory} />

      <Box sx={{ position: 'relative', width: '100%', maxWidth: 250, alignSelf: 'center' }}>
        <Box
          component="svg"
          viewBox="0 0 240 240"
          role="img"
          aria-label={`Direcția vântului: ${view.windDirectionLabel}`}
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        >
          <Box
            component="circle"
            cx="120"
            cy="120"
            r="98"
            fill="none"
            strokeWidth="8"
            sx={(theme) => ({ stroke: theme.palette.weather.line })}
          />
          {CARDINALS.map((point) => (
            <Box
              key={point.label}
              component="text"
              x={point.x}
              y={point.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="15"
              fontWeight="600"
              letterSpacing="1.4"
              sx={(theme) => ({
                fontFamily,
                fill: point.primary ? theme.palette.weather.soft : theme.palette.weather.faint,
              })}
            >
              {point.label}
            </Box>
          ))}
          <Box
            component="g"
            sx={{
              // Rotația se dă din CSS, nu prin atributul SVG `rotate(a cx cy)`:
              // acela nu e o funcție CSS, deci browserul interpolează cele două
              // stări ca matrice. Descompunerea unei diferențe apropiate de 180°
              // poate ieși ca o scalare prin zero — marcajul ar traversa centrul
              // în loc să se rotească. `rotate(Xdeg)` rămâne o rotație adevărată.
              transformBox: 'view-box',
              transformOrigin: '120px 120px',
              transform: `rotate(${angle}deg)`,
              transition: 'transform 1.4s cubic-bezier(.4,0,.2,1)',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            {/*
              Vârful arată spre centru, nu spre exterior. Direcția raportată e
              cea DIN care bate vântul (eticheta zice „Din nord-vest"), iar
              privitorul e în mijlocul busolei: marcajul stă pe inel acolo de
              unde vine vântul și arată către el.
            */}
            <Box
              component="path"
              d="M120 35 L133 9 L120 15 L107 9 Z"
              sx={(theme) => ({ fill: theme.palette.weather.accent })}
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              fontWeight: 600,
              fontSize: 'clamp(30px, 4vw, 40px)',
              lineHeight: 1,
              letterSpacing: '.02em',
            }}
          >
            {view.windDirShort}
          </Box>
          <Typography
            sx={(theme) => ({ fontSize: 14, fontWeight: 500, color: theme.palette.weather.soft })}
          >
            {view.windSpeed} km/h
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography
          sx={(theme) => ({
            fontSize: 13,
            fontWeight: 500,
            color: theme.palette.weather.soft,
            textAlign: 'center',
          })}
        >
          {view.windDirectionLabel}
        </Typography>
        <MeterBar width={view.windWidth} height={8} tone="accent" />
        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12,
            color: theme.palette.weather.faint,
          })}
        >
          <span>Viteză vânt</span>
          <span>0 – 60 km/h</span>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <StatTile label="Rafală" value={view.gustSpeed} />
        <StatTile label="Resimțit" value={view.beaufort} />
      </Box>
    </SurfaceCard>
  );
}
